/**
 * GenericReports — /project/:id/reports
 * Housing shell standard: numbered sections, full-width single-column cards.
 * Matches housing exactly: PRIMARY OUTPUT → Finance → TECHNICAL DOCUMENTATION.
 */
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, FileText, Download, Clock, CheckCircle2, Shield } from "lucide-react";
import { toast } from "sonner";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
export default function GenericReports() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(null);
  const {
    loading, isEmpty, sectorLabel, org, sector, calcVersion, journey,
    validationStatus, projectState,
  } = useProjectWorkspace(id);

  // CDE: tiered report status
  const reportState    = projectState?.report || {};
  const isPreliminary  = reportState.is_preliminary ?? true;
  const verLevel       = reportState.verification_level || "preliminary";
  const verLabel       = reportState.verification_label || "Preliminary — Not for investment use";
  const verBadge       = reportState.verification_badge || { label: "Preliminary", color: "amber" };
  const canPublish     = reportState.can_publish ?? false;

  const BADGE_COLORS = {
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    blue:    "bg-blue-50 border-blue-200 text-blue-700",
    teal:    "bg-teal-50 border-teal-200 text-teal-700",
    emerald: "bg-emerald-100 border-emerald-300 text-emerald-800",
  };
  const badgeCls = BADGE_COLORS[verBadge.color] || BADGE_COLORS.amber;

  const handleDownload = async (key, filename, endpoint) => {
    if (isEmpty) { toast.error("Run a calculation first."); return; }
    setDownloading(key);
    try {
      const token = localStorage.getItem("decarboniq_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/workspace/project/${id}/${endpoint}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Generation failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${filename} downloaded.`);
    } catch (e) {
      toast.error("Report generation failed.");
    }
    setDownloading(null);
  };

  const handleExportRegistryPack = async () => {
    if (isEmpty) { toast.error("Run a calculation first."); return; }
    setDownloading("registry-pack");
    try {
      const token = localStorage.getItem("decarboniq_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/workspace/project/${id}/export-registry-pack`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(org.name||"Project").slice(0,20)}-RegistryPack.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Registry pack downloaded.");
    } catch { toast.error("Export failed."); }
    setDownloading(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const isBiogas = sector === "biogas" || sector === "waste_recovery";
  const projName  = org.name || sectorLabel;

  // Housing-matched report card component
  function ReportCard({ number, label, badge, badgeColor, reports }) {
    return (
      <div className="mb-4">
        {/* Section header — numbered like housing */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-7 h-7 rounded-full bg-[#0F172A] flex items-center justify-center flex-shrink-0">
            <span className="text-[11px] font-bold text-white">{number}</span>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">{label}</p>
            <p className="text-xs text-slate-400">{badge}</p>
          </div>
        </div>

        {reports.map(r => (
              <div key={r.key}
            className="bg-white border border-slate-200 rounded-xl mb-3 overflow-hidden hover:border-teal-200 transition-colors"
            data-testid={`report-card-${r.key}`}>
            <div className="px-6 py-5">
              {/* Validation-Ready status label — visible at report card level */}
              <div className="flex items-center gap-1.5 mb-3 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                <Shield size={10} className="text-amber-500 flex-shrink-0" />
                <p className="text-xs text-amber-700 font-semibold">Validation-Ready Model Output — Prepared for independent third-party verification</p>
              </div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText size={16} className="text-teal-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                    {r.badge && (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${r.badgeClass || "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>
                        {r.badge}
                      </span>
                    )}
                    <span className="text-xs text-slate-400 ml-auto">{r.pages}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{r.desc}</p>
                </div>
              </div>
              {/* Download button — full width like housing */}
              <button
                onClick={() => handleDownload(r.key, r.filename, r.endpoint)}
                disabled={downloading === r.key || isEmpty}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors"
                data-testid={`download-${r.key}`}>
                {downloading === r.key
                  ? <><RefreshCw size={13} className="animate-spin" /> Generating...</>
                  : <><Download size={13} /> Download PDF</>}
              </button>
              {calcVersion && (
                <p className="text-xs text-slate-400 mt-2 text-center italic">
                  Based on v{calcVersion.number} · {calcVersion.hash?.slice(0,10)}...
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Grouped report sections — matches housing structure
  const SECTION_1_REPORTS = [
    {
      key:       "scoping",
      label:     "Phase 1 Scoping & Boundary Report",
      badge:     "New",
      badgeClass:"bg-emerald-100 text-emerald-700 border-emerald-200",
      desc:      "16-page institutional scoping document: project description, system boundary, baseline/project scenario, methodology, data inputs, emissions reduction, additionality, carbon pathways, PoA potential, risks, and sign-off section. Suitable for investor briefing, VVB pre-engagement, and government disclosure.",
      pages:     "16 pages",
      endpoint:  "scoping-report",
      filename:  `${(org.name||"Project").slice(0,20)}-Phase1-ScopingReport.pdf`,
    },
    {
      key:       "credentials",
      label:     "Climate & Circularity Report",
      badge:     "Primary",
      badgeClass:"bg-teal-100 text-teal-700 border-teal-200",
      desc:      "Institutional-grade report covering emissions (Scope 1–3 where available), circularity performance, SDG impact, and data integrity — aligned with IFRS S2, GHG Protocol, and PCAF.",
      pages:     "12-18 pages",
      endpoint:  "credentials-report",
      filename:  `${(org.name||"Project").slice(0,20)}-Climate-Circularity-Report.pdf`,
    },
  ];

  const SECTION_2_REPORTS = [
    ...(isBiogas ? [{
      key:       "finance-brief",
      label:     "Finance Brief",
      badge:     "For Banks",
      badgeClass:"bg-blue-100 text-blue-700 border-blue-200",
      desc:      "Bank-facing financing document. DSCR, deal pathways, sensitivity, restructuring recommendations, revenue model. For DFI pre-screening and investment assessment.",
      pages:     "6 pages",
      endpoint:  "biogas-finance-brief",
      filename:  `${(org.name||"Project").slice(0,20)}-FinanceBrief.pdf`,
    }] : []),
  ];

  const SECTION_3_REPORTS = [
    {
      key:      "pdd",
      label:    "Project Design Document (PDD)",
      badge:    "Draft v0.1",
      badgeClass:"bg-amber-100 text-amber-700 border-amber-200",
      desc:     "26-section verifier-grade PDD. Covers project identification, technology, baseline/project scenario, additionality, monitoring, SDG, circularity, risk assessment, and validation readiness. Institutionally written.",
      pages:    "Multi-page",
      endpoint: "pdd",
      filename: `${(org.name||"Project").slice(0,20)}-PDD.pdf`,
    },
    {
      key:      "mrv",
      label:    "MRV Plan",
      badge:    "Technical",
      badgeClass:"bg-slate-100 text-slate-600 border-slate-200",
      desc:     "Monitoring, Reporting & Verification plan. Parameter table (1:1 evidence vault mapping), measurement methods, QA/QC procedures, verification readiness checklist.",
      pages:    "Multi-page",
      endpoint: "mrv-plan",
      filename: `${(org.name||"Project").slice(0,20)}-MRVPlan.pdf`,
    },
    {
      key:      "emissions",
      label:    "Emissions Calculation Report",
      badge:    "Auditable",
      badgeClass:"bg-slate-100 text-slate-600 border-slate-200",
      desc:     "Full formula breakdown with factor audit trail. Inputs, IPCC/IEA sources, calculation steps, results, sensitivity. Version-linked with SHA-256 hash for traceability.",
      pages:    "Multi-page",
      endpoint: "emissions-report",
      filename: `${(org.name||"Project").slice(0,20)}-EmissionsReport.pdf`,
    },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6">

        <button onClick={() => navigate(`/project/${id}/workspace`)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> {sectorLabel}
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <PageHeader label={projName} title="Reports" />
            <p className="text-xs text-slate-400 mt-0.5">
              {isEmpty ? "Run a calculation to generate reports" : "Available reports · " + (calcVersion ? `Calc v${calcVersion.number}` : "")}
            </p>
          </div>
          {/* Tiered verification badge (CDE-driven) */}
          <div className={`border rounded-xl px-4 py-2.5 text-right flex-shrink-0 ${badgeCls}`} data-testid="verification-tier-badge">
            <p className="text-xs font-bold uppercase tracking-wide">{verBadge.label}</p>
            <p className="text-xs opacity-70 mt-0.5 max-w-40">Validation-Ready Model — Independent verification required</p>
          </div>
        </div>

        {/* Tiered verification label banner */}
        {!isEmpty && (
          <div className={`border-2 rounded-xl px-5 py-3 mb-5 flex items-start gap-3 ${
            verLevel === "certified"           ? "border-emerald-400 bg-emerald-50" :
            verLevel === "verified_independent"? "border-emerald-300 bg-emerald-50" :
            verLevel === "reviewed_internal"   ? "border-teal-300 bg-teal-50" :
            verLevel === "uploaded"            ? "border-blue-300 bg-blue-50" :
                                                 "border-amber-400 bg-amber-50"
          }`} data-testid="verification-banner">
            <Shield size={16} className={`flex-shrink-0 mt-0.5 ${
              verLevel === "reviewed_internal" || verLevel === "certified" || verLevel === "verified_independent"
                ? "text-teal-600" : "text-amber-600"
            }`} />
            <div className="flex-1">
              <p className={`text-sm font-bold uppercase tracking-wide ${
                verLevel === "reviewed_internal" ? "text-teal-800" : "text-amber-800"
              }`}>{verLabel}</p>
              <p className="text-xs text-slate-600 mt-0.5">
                {verLevel === "reviewed_internal"
                  ? "Reviewed by internal administrator. For independent verification, engage an accredited VVB or third-party verifier."
                  : verLevel === "verified_independent"
                  ? "Independently verified. Suitable for DFI submission and carbon market pre-engagement."
                  : verLevel === "certified"
                  ? "Fully certified by accredited body. Eligible for carbon credit issuance."
                  : verLevel === "uploaded"
                  ? "Evidence submitted. Pending admin review before readiness assessment."
                  : "Reports are platform-generated and internally consistent. Not equivalent to Gold Standard, VERRA, or CDM validation."}
              </p>
              {/* Verifier credentials — shown at T3+ */}
              {verLevel === "verified_independent" && (() => {
                const creds = reportState.verifier_creds;
                const verName = reportState.verifier_name;
                const verOrg  = reportState.verifier_org;
                const verAt   = reportState.verified_at?.slice(0,10);
                const credLabel = creds?.credential_label || (verOrg ? `Independently Reviewed by ${verOrg}` : "");
                return credLabel ? (
                  <div className="mt-3 pt-3 border-t border-teal-200 space-y-1">
                    <p className="text-xs font-bold text-teal-800" data-testid="verifier-credential-label">{credLabel}</p>
                    {creds?.accreditations?.length > 0 && (
                      <p className="text-xs text-teal-600">Accreditations: {creds.accreditations.join(", ")}</p>
                    )}
                    {verAt && <p className="text-xs text-teal-500">Verified: {verAt}</p>}
                    {reportState.decision_hash && (
                      <p className="text-xs text-slate-400 font-mono">Audit ref: {reportState.decision_hash}</p>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${badgeCls}`}>
              {verBadge.label}
            </span>
          </div>
        )}

        {/* Export Registry Pack */}
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 mb-6 flex items-center justify-between" data-testid="export-registry-pack">
          <div>
            <p className="text-xs font-bold text-slate-900">Export Registry Submission Pack</p>
            <p className="text-xs text-slate-500 mt-0.5">ZIP: PDD + MRV Plan + Emissions Report + Evidence Index + Metadata JSON</p>
          </div>
          <button onClick={handleExportRegistryPack}
            disabled={downloading === "registry-pack" || isEmpty}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50 flex-shrink-0 ml-4"
            data-testid="export-btn">
            {downloading === "registry-pack"
              ? <><RefreshCw size={11} className="animate-spin" /> Generating...</>
              : <><Download size={11} /> Export ZIP</>}
          </button>
        </div>

        {/* Numbered report sections — housing-matched structure */}
        <ReportCard number="1" label="PRIMARY OUTPUT"
          badge="Core credibility document — for regulators, DFI reviewers, and ESG teams"
          reports={SECTION_1_REPORTS} />

        {SECTION_2_REPORTS.length > 0 && (
          <ReportCard number="2" label="FOR BANKS &amp; INVESTORS"
            badge="Decision support for lenders and green finance pre-appraisal"
            reports={SECTION_2_REPORTS} />
        )}

        <ReportCard number={SECTION_2_REPORTS.length > 0 ? "3" : "2"} label="TECHNICAL DOCUMENTATION"
          badge="Supporting documents for carbon structuring, MRV, and validation"
          reports={SECTION_3_REPORTS} />

        <p className="text-xs text-slate-400 italic text-center mt-4 pb-2">
          Reports are Validation-Ready Model outputs — platform-generated and internally consistent.
          They are NOT equivalent to Gold Standard, Verra VCS, or CDM validation. Independent third-party verification is required before any carbon credit issuance or formal DFI submission.
        </p>

    </div>
  );
}
